import { useEffect, useMemo } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import Breadcrumbs from 'components/breadcrumb'
import styles from './question-results-page.module.css'
import { NextQuestionButton, PreviousQuestionButton } from 'components/blue-button'
import { QuestionRow } from 'components/question-row'
import { useGetQuestionOverviewQuery } from 'util/rtk-api'
import { QuestionResultsContextProvider, useQuestionResultsContext } from './question-results-context'
import { StudentLessonResultContextProvider } from 'contexts/student-lesson-result-context'
import { verifyLogin } from 'util/cache'
import { Difficulty, difficultyToCssClass, SECTION_SHORT_HEADERS, SECTIONS } from '@all-for-one/shared-types'

const DifficultyTag = ({ difficulty }: { difficulty?: string }) => {
    const label = difficulty
    const cssClass = useMemo(() => {
        const d = label as Difficulty
        return difficultyToCssClass[d]
    }, [label])
    return <span className={styles[cssClass]}>{label}</span>
}

const QuestionResultsInner = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { classId, lessonId, questionId, overview } = useQuestionResultsContext()
    const section = useMemo(() => new URLSearchParams(location.search).get('section') || undefined, [location.search])
    const difficulty = useMemo(() => new URLSearchParams(location.search).get('difficulty') || undefined, [location.search])
    const { data, isLoading, error } = useGetQuestionOverviewQuery({ classId, lessonId, questionId, section: section as SECTIONS })
    // overview is provided by context

    useEffect(() => { verifyLogin(navigate) })

    const filteredQuestions = useMemo(() => {
        if (!overview?.questions) return []
        if (!difficulty) return overview.questions
        return overview.questions.filter(q => q.difficulty === difficulty)
    }, [overview, difficulty])

    const previous = useMemo(() => {
        if (!filteredQuestions.length || !data?.questionId) return undefined
        const idx = filteredQuestions.findIndex(q => q.questionId === data.questionId && q.section === data.section)
        if (idx <= 0) return undefined
        const q = filteredQuestions[idx - 1]
        return { questionId: q.questionId, section: q.section }
    }, [filteredQuestions, data])

    const next = useMemo(() => {
        if (!filteredQuestions.length || !data?.questionId) return undefined
        const idx = filteredQuestions.findIndex(q => q.questionId === data.questionId && q.section === data.section)
        if (idx === -1 || idx >= filteredQuestions.length - 1) return undefined
        const q = filteredQuestions[idx + 1]
        return { questionId: q.questionId, section: q.section }
    }, [filteredQuestions, data])

    if (isLoading) return <div className={styles.container}>Loading...</div>
    if (error || !data) return <div className={styles.container}>Unable to load question results.</div>

    return (
        <div className={styles.container}>
            <Breadcrumbs items={[
                { label: 'My Classrooms', path: '/classrooms' },
                { label: data.className, path: `/classrooms/${data.classId}` },
                { label: data.lessonName, path: `/classrooms/${data.classId}/lessons/${data.lessonId}${difficulty ? `?difficulty=${encodeURIComponent(difficulty)}` : ''}` },
            ]} />

            <div className={styles.row}>
                <div className={styles.headerLeft}>
                    <DifficultyTag difficulty={data.difficulty} />
                    {data.sectionAvgPercent !== undefined && (
                        <span>{SECTION_SHORT_HEADERS[data.section]} avg: <span className={styles.bold}>{data.sectionAvgPercent}%</span></span>
                    )}
                    <span>This question: <span className={styles.bold}>{data.overallPercent}%</span></span>
                </div>
            </div>

            <div className={styles.questionBlock}>
                <div className={styles.bold}>Question {data.displayLabel ?? (data.questionIndex! + 1)}</div>
                <div className={styles.questionText}>{data.questionText}</div>
            </div>

            {/* Todo: update the CSS to like My Classrooms - Question selected in grades */}
            <div className={styles.resultsContainer}>
                {data.results.map((r, idx) => (
                    <StudentLessonResultContextProvider key={r.studentId} studentId={r.studentId} lessonId={data.lessonId} classId={classId}>
                        <QuestionRow
                            questionId={data.questionId}
                            index={idx + 1}
                            question={data.questionText || ''}
                            answer={r.answer}
                            correct={r.correct}
                            score={r.score}
                            manualScore={r.manualScore}
                            manuallyGraded={r.manuallyGraded}
                            correctAnswer={data.correctAnswer}
                            studentName={r.studentName}
                        />
                    </StudentLessonResultContextProvider>
                ))}
            </div>

            <div className={styles.navRow}>
                <PreviousQuestionButton onClick={() => previous && navigate(`/classrooms/${data.classId}/lessons/${data.lessonId}/questions/${previous.questionId}?section=${encodeURIComponent(previous.section || '')}&difficulty=${encodeURIComponent(difficulty || '')}`)} visible={!!previous}/>
                <NextQuestionButton onClick={() => next && navigate(`/classrooms/${data.classId}/lessons/${data.lessonId}/questions/${next.questionId}?section=${encodeURIComponent(next.section || '')}&difficulty=${encodeURIComponent(difficulty || '')}`)} visible={!!next} />
            </div>
        </div>
    )
}

export const QuestionResultsPage = () => {
    const { classId, lessonId, questionId } = useParams()
    return (
        <QuestionResultsContextProvider classId={classId!} lessonId={lessonId!} questionId={questionId!}>
            <QuestionResultsInner />
        </QuestionResultsContextProvider>
    )
}


